import React from "react";
import { Link } from "react-router-dom";
import { HardHat, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "August 12, 2026";
const COMPANY_NAME   = "Builders Binder";
const CONTACT_EMAIL  = "support@buildersbinder.com";
const SITE_URL       = "https://buildersbinder.com";

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-white font-sans text-zinc-800">
      {/* Nav */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-amber-500 rounded-sm flex items-center justify-center">
              <HardHat size={15} className="text-zinc-950" />
            </div>
            <span className="font-bold text-sm tracking-tight">{COMPANY_NAME}</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900">
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">Legal</p>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Refund Policy</h1>
          <p className="text-sm text-zinc-500">Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last updated: {EFFECTIVE_DATE}</p>
        </div>

        <div className="prose prose-zinc max-w-none space-y-8 text-[15px] leading-relaxed">

          <section>
            <p>
              This Refund Policy explains when and how <strong>{COMPANY_NAME}</strong> ("we," "our," or "us")
              issues refunds for paid subscriptions to{" "}
              <a href={SITE_URL} className="underline text-zinc-700">{SITE_URL}</a> (the "Service").
              By subscribing to the Service, you agree to this policy, which is incorporated into our{" "}
              <Link to="/terms" className="underline text-zinc-700">Terms of Service</Link>.
            </p>
          </section>

          <Section title="1. Free Trial">
            <p>
              All new accounts receive a <strong>7-day free trial</strong> with no credit card required.
              We encourage you to fully explore the Service during this period before subscribing to a paid plan.
              Because no payment is taken during the trial, no refund is applicable.
            </p>
          </Section>

          <Section title="2. 14-Day Refund Guarantee — No Exceptions">
            <p>
              {COMPANY_NAME} sells the Service through <strong>Paddle.com</strong>, our authorized reseller and
              Merchant of Record. In line with the minimum refund window set out in Paddle's Buyer Terms, we
              guarantee a full refund of your Charges if you request it within <strong>14 days of the relevant
              charge</strong> — for any reason, with <strong>no exceptions</strong>.
            </p>
            <p className="mt-2">
              This 14-day guarantee applies to your first payment on a new subscription and to each subsequent
              renewal charge. You do not need to explain why you are requesting a refund, and we will not ask
              you to justify it. Once approved, we process the refund through Paddle to your original payment
              method.
            </p>
            <p className="mt-2">
              Refund requests received <strong>more than 14 days</strong> after the relevant charge are not
              eligible under this policy, except where applicable law in your territory of purchase requires
              otherwise (see Paddle's Buyer Terms, Country-Specific Terms).
            </p>
          </Section>

          <Section title="3. Subscription Plans and Pricing">
            <p>Paid plans are billed monthly at the following rates:</p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm border border-zinc-200 rounded-sm">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-700 border-b border-zinc-200">Plan</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-700 border-b border-zinc-200">Price</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-700 border-b border-zinc-200">Best for</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  <tr>
                    <td className="px-4 py-3 font-medium">Starter</td>
                    <td className="px-4 py-3">$19 / month</td>
                    <td className="px-4 py-3 text-zinc-500">Small GCs, up to 5 jobs</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Pro</td>
                    <td className="px-4 py-3">$39 / month</td>
                    <td className="px-4 py-3 text-zinc-500">Growing teams, up to 25 jobs</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Agency</td>
                    <td className="px-4 py-3">$89 / month</td>
                    <td className="px-4 py-3 text-zinc-500">Agencies and high-volume contractors</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm text-zinc-500">
              All prices are in US dollars. Subscriptions renew automatically each month unless cancelled before
              the renewal date via the Paddle billing portal in Account Settings.
            </p>
          </Section>

          <Section title="4. How to Cancel">
            <p>
              You may cancel your subscription at any time through the <strong>Paddle billing portal</strong>{" "}
              accessible from your Account Settings page. Cancellation stops future charges immediately.
              You will retain full access to your plan features until the end of the current paid billing period.
              We do not charge cancellation fees.
            </p>
            <p className="mt-2">
              Cancelling your subscription does not delete your account or your tracked job data. You can
              resubscribe at any time and your data will be waiting for you.
            </p>
          </Section>

          <Section title="5. How to Request a Refund">
            <p>To request a refund under Section 2, please email us at:</p>
            <address className="not-italic mt-3 text-sm bg-zinc-50 border border-zinc-200 rounded-sm p-4">
              <strong>{COMPANY_NAME} — Billing Support</strong><br />
              Email: <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a><br />
              Subject line: <span className="font-mono text-xs bg-zinc-100 px-1 rounded">Refund Request – [your account email]</span>
            </address>
            <p className="mt-3">Please include:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>The email address associated with your account.</li>
              <li>The date and approximate amount of the charge in question.</li>
            </ul>
            <p className="mt-2 text-sm text-zinc-500">
              No reason is required — requests made within 14 days of the charge are approved automatically.
            </p>
            <p className="mt-3">
              We aim to respond to all refund requests within <strong>3 business days</strong>. Approved refunds
              are processed through Paddle and typically appear on your original payment method within
              5–10 business days, depending on your bank or card issuer.
            </p>
          </Section>

          <Section title="6. Chargebacks">
            <p>
              We ask that you contact us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a>{" "}
              before initiating a chargeback with your bank or card issuer. We are almost always able to
              resolve billing disputes faster and more easily directly. Initiating a chargeback without
              first contacting us may result in suspension of your account while the dispute is under review.
            </p>
          </Section>

          <Section title="7. Changes to This Policy">
            <p>
              We may update this Refund Policy from time to time. When we make material changes, we will
              provide at least <strong>14 days' advance notice</strong> by email or a prominent in-app notice.
              Your continued use of the Service after the effective date of the revised policy constitutes
              your acceptance of the changes.
            </p>
          </Section>

          <Section title="8. Contact Us">
            <p>Questions about this Refund Policy? Reach us at:</p>
            <address className="not-italic mt-2 text-sm bg-zinc-50 border border-zinc-200 rounded-sm p-4">
              <strong>{COMPANY_NAME}</strong><br />
              Email: <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-zinc-700">{CONTACT_EMAIL}</a><br />
              Website: <a href={SITE_URL} className="underline text-zinc-700">{SITE_URL}</a>
            </address>
          </Section>

        </div>
      </main>

      <footer className="border-t border-zinc-200 py-8 mt-12 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-zinc-500">
          <span>© {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.</span>
          <nav className="flex gap-5">
            <Link to="/privacy"  className="hover:text-zinc-950 underline underline-offset-2">Privacy Policy</Link>
            <Link to="/terms"    className="hover:text-zinc-950 underline underline-offset-2">Terms of Service</Link>
            <Link to="/refunds"  className="hover:text-zinc-950 underline underline-offset-2">Refund Policy</Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-zinc-950">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-bold tracking-tight text-zinc-950 border-b border-zinc-100 pb-2">{title}</h2>
      {children}
    </section>
  );
}
